import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import AllowedDomains from "@/components/AllowedDomains";
import ReassignFacilitiesDialog from "@/components/ReassignFacilitiesDialog";

export default function Users() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [reassignUser, setReassignUser] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "rep" });

  const load = () => api.get("/users").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/auth/register", form); toast.success("User added"); setOpen(false); setForm({ email: "", name: "", password: "", role: "rep" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    if (u.active) {
      // Disabling — trigger reassignment flow
      setReassignUser(u);
      return;
    }
    await api.patch(`/users/${u.user_id}`, { active: true });
    load();
  };
  const changeRole = async (u, role) => { await api.patch(`/users/${u.user_id}`, { role }); load(); };

  const submitReset = async () => {
    if (resetPwd.length < 8) { toast.error("Use at least 8 characters"); return; }
    try {
      await api.post(`/users/${resetUser.user_id}/reset-password`, { new_password: resetPwd });
      toast.success("Password reset — share the new password securely with the user.");
      setResetUser(null); setResetPwd("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Team</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black">Users</h1>
          <p className="text-slate-500 mt-1 text-sm">Unlimited seats for your behavioral health team.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="user-new-btn"><Plus className="w-4 h-4 mr-1" /> Add user</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add user</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" data-testid="user-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Temporary password</Label><Input type="text" data-testid="user-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="rep">Rep</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="user-save" onClick={submit} className="bg-slate-900 hover:bg-slate-800">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest font-mono-plex text-slate-500">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Provider</th><th className="p-3">Active</th><th className="p-3">Password</th></tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.user_id} className="border-t border-slate-100 row-enter">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 font-mono-plex text-xs">{u.email}</td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={v => changeRole(u, v)}>
                    <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="rep">Rep</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-xs"><Badge variant="outline">{u.auth_provider || "jwt"}</Badge></td>
                <td className="p-3"><Button size="sm" variant={u.active ? "outline" : "default"} onClick={() => toggleActive(u)}>{u.active ? "Active" : "Disabled"}</Button></td>
                <td className="p-3">
                  <Button size="sm" variant="ghost" onClick={() => { setResetUser(u); setResetPwd(""); }} data-testid={`user-reset-${u.user_id}`}>
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {resetUser?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Set a temporary password (min 8 chars). Share it securely with the user; they can change it after signing in.</p>
          <div>
            <Label>New password</Label>
            <Input type="text" data-testid="admin-reset-pwd" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="mt-1.5" placeholder="Temporary password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
            <Button onClick={submitReset} data-testid="admin-reset-submit" className="bg-slate-900 hover:bg-slate-800">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-8">
        <AllowedDomains />
      </div>

      <ReassignFacilitiesDialog
        user={reassignUser}
        open={!!reassignUser}
        onOpenChange={(v) => !v && setReassignUser(null)}
        onDone={() => { setReassignUser(null); load(); }}
      />
    </div>
  );
}
